import { FC } from "react";
import {
	Autocomplete,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
} from "@mui/material";
import { EventRatingsEvent } from "../../../../../services/reports.service";

export type ViewMode = "population" | "single";

/**
 * SIMS-9019 — one selectable enrollment, already reduced to the single line
 * that identifies it.
 *
 * The bar stays presentational: `useParticipantColumn.optionLabel` owns the
 * "CIN — Name" shape AND the account's decision about whether a name may be
 * shown at all, so that privacy rule is not re-implemented per consumer. The
 * bar receives the finished string.
 */
export interface EnrollmentOption {
	enrollmentId: string;
	label: string;
}

interface EventRatingsSelectorBarProps {
	idPrefix: string;
	events: EventRatingsEvent[];
	enrollments: EnrollmentOption[];
	eventId: string;
	viewMode: ViewMode;
	enrollmentId: string;
	onChange: (next: {
		eventId?: string;
		viewMode?: ViewMode;
		enrollmentId?: string;
	}) => void;
}

const EventRatingsSelectorBar: FC<EventRatingsSelectorBarProps> = ({
	idPrefix,
	events,
	enrollments,
	eventId,
	viewMode,
	enrollmentId,
	onChange,
}) => {
	return (
		<Stack
			direction={{ xs: "column", md: "row" }}
			spacing={2}
			alignItems={{ xs: "stretch", md: "center" }}
		>
			<FormControl size="small" sx={{ minWidth: 220 }}>
				<InputLabel id={`${idPrefix}-event-label`}>Event</InputLabel>
				<Select
					labelId={`${idPrefix}-event-label`}
					label="Event"
					value={eventId}
					onChange={(e) => onChange({ eventId: e.target.value })}
					disabled={events.length === 0}
				>
					{events.map((ev) => (
						<MenuItem key={ev.eventId} value={ev.eventId}>
							{ev.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<ToggleButtonGroup
				size="small"
				exclusive
				value={viewMode}
				onChange={(_e, next: ViewMode | null) => {
					if (next) onChange({ viewMode: next });
				}}
				aria-label="View mode"
			>
				<ToggleButton value="population" aria-label="Population average">
					Population
				</ToggleButton>
				<ToggleButton value="single" aria-label="Single enrollment">
					Single enrollment
				</ToggleButton>
			</ToggleButtonGroup>

			{viewMode === "single" && (
				/* SIMS-9019: an Autocomplete rather than a Select, because the list is
				   one entry per enrolled participant and the ticket asks that it be
				   searchable. Both the CIN and the name live in `label`, so MUI's
				   default substring filter already matches either one. */
				<Autocomplete
					id={`${idPrefix}-enrollment`}
					size="small"
					sx={{ minWidth: 260 }}
					options={enrollments}
					getOptionLabel={(option) => option.label}
					// Controlled by id, so the option object is looked up rather than
					// held in state — the list is rebuilt whenever the report refetches
					// and a stale object would stop matching by reference.
					value={
						enrollments.find((en) => en.enrollmentId === enrollmentId) ?? null
					}
					isOptionEqualToValue={(option, selected) =>
						option.enrollmentId === selected.enrollmentId
					}
					onChange={(_event, next) =>
						onChange({ enrollmentId: next?.enrollmentId ?? "" })
					}
					disabled={enrollments.length === 0}
					// Keyed by enrollment id, not by label. Autocomplete defaults the
					// option key to `getOptionLabel`, and two enrollments CAN share a
					// label — the same participant enrolled twice reads identically.
					// React then warns "two children with the same key" and reserves the
					// right to duplicate or omit one of them, which would make an
					// enrollment unselectable.
					renderOption={(props, option) => (
						<li {...props} key={option.enrollmentId}>
							{option.label}
						</li>
					)}
					renderInput={(params) => (
						<TextField {...params} label="CIN/Enrollment" />
					)}
				/>
			)}
		</Stack>
	);
};

export default EventRatingsSelectorBar;
